import "./RemoteControl.css";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  formatDurationSeconds,
  resolveTargetSeconds,
  type PresentSignal,
  type PresentSignalInput,
  type SurfSlide,
} from "@vida/surf-core";
import { useSurfLibrary } from "../../hooks/useSurfLibrary";
import { useSurfAssetUrls } from "../../hooks/useSurfAssetUrls";
import { SlideCanvas } from "../editor/SlideCanvas";
import { SpeakerScriptView } from "../editor/SpeakerScriptEditor";
import { useRemoteChannel } from "./useRemoteChannel";

const SWIPE_THRESHOLD_PX = 48;

function slideLabel(slide: SurfSlide, index: number): string {
  const title = slide.layers
    .find((l) => l.type === "text" && l.textRole === "title")
    ?.content?.trim();
  return title || `Slide ${index + 1}`;
}

function readSessionIdFromHash(): string {
  if (typeof window === "undefined") return "";
  const hash = window.location.hash.replace(/^#/, "");
  const params = new URLSearchParams(hash);
  return (params.get("s") ?? "").trim();
}

export function RemoteControl() {
  const { deckId } = useParams<{ deckId: string }>();
  const { getDeck } = useSurfLibrary();
  const row = deckId ? getDeck(deckId) : undefined;
  const deck = row?.deck;
  const total = deck?.slides.length ?? 0;

  const [sessionId] = useState(() => readSessionIdFromHash());
  const [index, setIndex] = useState(0);
  const [blanked, setBlanked] = useState(false);
  const [elapsedSlide, setElapsedSlide] = useState(0);

  const indexRef = useRef(index);
  indexRef.current = index;
  const handshakeDoneRef = useRef(false);
  const peerPresentRef = useRef(false);
  const slideStartedRef = useRef(Date.now());
  const touchStartXRef = useRef<number | null>(null);

  const { backgroundUrls, layerImageUrls } = useSurfAssetUrls(deck ?? null, {
    readerMode: true,
  });

  const onSignal = useCallback((signal: PresentSignal) => {
    // Display announces its current index on connect (ping). Align once.
    if (signal.action === "ping" && signal.slideIndex != null && !handshakeDoneRef.current) {
      handshakeDoneRef.current = true;
      setIndex((prev) => (prev === signal.slideIndex ? prev : signal.slideIndex!));
    }
  }, []);

  const getHeartbeatSignal = useCallback((): PresentSignalInput | null => {
    if (!sessionId || !deckId) return null;
    // Durante la finestra di riallineamento (dopo una riconnessione del telefono,
    // prima di risentire il display) inviamo "ping" (presenza) invece di "goto",
    // per non trascinare il display al nostro indice stantio. Dopo l'handshake
    // (o mentre il peer è presente) torniamo a "goto".
    const realigning = !handshakeDoneRef.current && !peerPresentRef.current;
    return {
      sessionId,
      deckId,
      action: realigning ? "ping" : "goto",
      slideIndex: indexRef.current,
      senderRole: "remote",
    };
  }, [sessionId, deckId]);

  const channel = useRemoteChannel({
    sessionId: sessionId || null,
    deckId: deckId ?? "",
    role: "remote",
    onSignal,
    getHeartbeatSignal,
  });

  const { sendGoto, sendBlank, sendUnblank, sendPing } = channel;

  // A3: track peer presence in a ref (read by the heartbeat) and reset the
  // handshake when the display disappears (true→false). Al ritorno del display
  // (che rimanda "ping" col suo indice) il phone si riallinea invece di imporre
  // il proprio indice stantio.
  const channelPeerPresent = channel.peerPresent;
  useEffect(() => {
    const was = peerPresentRef.current;
    peerPresentRef.current = channelPeerPresent;
    if (was && !channelPeerPresent) {
      handshakeDoneRef.current = false;
    }
  }, [channelPeerPresent]);

  // Handshake: announce ourselves + starting index once the channel exists.
  useEffect(() => {
    if (!sessionId) return;
    sendPing(indexRef.current);
    sendGoto(indexRef.current);
  }, [sessionId, sendPing, sendGoto]);

  useEffect(() => {
    slideStartedRef.current = Date.now();
    setElapsedSlide(0);
  }, [index]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setElapsedSlide(Math.floor((Date.now() - slideStartedRef.current) / 1000));
    }, 250);
    return () => window.clearInterval(id);
  }, []);

  const goTo = useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.min(total - 1, next));
      setIndex(clamped);
      sendGoto(clamped);
    },
    [total, sendGoto],
  );

  const goPrev = useCallback(() => goTo(indexRef.current - 1), [goTo]);
  const goNext = useCallback(() => goTo(indexRef.current + 1), [goTo]);

  const toggleBlank = useCallback(() => {
    setBlanked((prev) => {
      const next = !prev;
      if (next) sendBlank();
      else sendUnblank();
      return next;
    });
  }, [sendBlank, sendUnblank]);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0]?.clientX ?? null;
  }, []);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const start = touchStartXRef.current;
      touchStartXRef.current = null;
      if (start == null) return;
      const endX = e.changedTouches[0]?.clientX ?? start;
      const dx = endX - start;
      if (dx <= -SWIPE_THRESHOLD_PX) goNext();
      else if (dx >= SWIPE_THRESHOLD_PX) goPrev();
    },
    [goNext, goPrev],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext]);

  const slide = deck?.slides[index];
  const nextSlide = deck && index < total - 1 ? deck.slides[index + 1] : undefined;
  const scriptText = slide?.speakerScript?.text?.trim() ?? "";
  const targetSeconds = deck && slide ? resolveTargetSeconds(deck, slide.id) : 60;
  const remaining = Math.max(0, targetSeconds - elapsedSlide);
  const timerStatus =
    targetSeconds > 0 && elapsedSlide / targetSeconds > 1.5
      ? "over"
      : targetSeconds > 0 && elapsedSlide / targetSeconds > 1
        ? "warn"
        : "ok";

  const nextLabel = useMemo(
    () => (nextSlide ? slideLabel(nextSlide, index + 1) : ""),
    [nextSlide, index],
  );

  if (!deckId) {
    return (
      <div className="remote-empty">
        <p>Deck non specificato.</p>
        <Link to="/">← Libreria</Link>
      </div>
    );
  }

  if (!sessionId) {
    return (
      <div className="remote-empty">
        <p>Sessione telecomando mancante. Riapri il QR dal display in modalità presentazione.</p>
        <Link to={`/deck/${deckId}`}>← Editor</Link>
      </div>
    );
  }

  if (!deck) {
    return (
      <div className="remote-empty">
        <p>{navigator.onLine ? "Deck in caricamento…" : "Deck non disponibile offline."}</p>
        <Link to={`/deck/${deckId}`}>← Editor</Link>
      </div>
    );
  }

  if (!total || !slide) {
    return (
      <div className="remote-empty">
        <p>Nessuna slide nel deck.</p>
        <Link to={`/deck/${deckId}`}>← Editor</Link>
      </div>
    );
  }

  return (
    <div className="remote-control">
      <header className="remote-header">
        <div className="remote-header-title">
          <span className="remote-deck-title">{deck.title}</span>
          <span
            className={`remote-status remote-status--${channel.peerPresent ? "on" : "off"}`}
            role="status"
          >
            {channel.peerPresent ? "Display connesso" : "Display non connesso"}
          </span>
          {channel.lastError && (
            <span className="remote-status-error" role="status">
              Errore canale: {channel.lastError}
            </span>
          )}
        </div>
        <span className="remote-counter">
          {index + 1} / {total}
        </span>
      </header>

      <main
        className="remote-stage"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        aria-label="Area swipe: scorri per cambiare slide"
      >
        <button
          type="button"
          className="remote-tapzone remote-tapzone--prev"
          aria-label="Slide precedente"
          disabled={index <= 0}
          onClick={goPrev}
        />
        <div className="remote-thumb">
          <SlideCanvas
            deck={deck}
            slide={slide}
            slideBackgroundUrl={backgroundUrls[slide.id] ?? null}
            layerImageUrls={layerImageUrls}
            selectedLayerIds={[]}
            showGuides={false}
            readOnly
            compact
            onSelectLayer={() => undefined}
            onLayerRect={() => undefined}
            onLayerContent={() => undefined}
          />
        </div>
        <button
          type="button"
          className="remote-tapzone remote-tapzone--next"
          aria-label="Slide successiva"
          disabled={index >= total - 1}
          onClick={goNext}
        />
      </main>

      <div className={`remote-timers remote-timers--${timerStatus}`}>
        <span>Slide {formatDurationSeconds(elapsedSlide)} / {formatDurationSeconds(targetSeconds)}</span>
        <span>−{formatDurationSeconds(remaining)}</span>
      </div>

      <section className="remote-teleprompter" aria-label="Discorso speaker">
        {scriptText ? (
          <SpeakerScriptView markdown={scriptText} className="remote-teleprompter-md" />
        ) : (
          <p className="muted">Nessun discorso per questa slide.</p>
        )}
      </section>

      {nextSlide && (
        <div className="remote-next">
          <span className="muted">Prossima</span>
          <strong>{nextLabel}</strong>
        </div>
      )}

      <footer className="remote-footer">
        <button
          type="button"
          className="surf-btn surf-btn--ghost remote-nav-btn"
          disabled={index <= 0}
          onClick={goPrev}
        >
          ◀
        </button>
        <button
          type="button"
          className={`surf-btn remote-blank-btn${blanked ? " is-active" : ""}`}
          onClick={toggleBlank}
        >
          {blanked ? "Mostra" : "Schermo nero"}
        </button>
        <button
          type="button"
          className="surf-btn surf-btn--ghost remote-nav-btn"
          disabled={index >= total - 1}
          onClick={goNext}
        >
          ▶
        </button>
      </footer>
    </div>
  );
}
